<template>
  <div class="account-analysis-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-content">
        <div class="hero-text">
          <h1>🎯 账号分析中心</h1>
          <p>输入社交媒体账号，AI 深度分析创作风格、爆款秘诀，助你快速学习和模仿优秀创作者。</p>
          <div class="hero-badges">
            <span>深度洞察</span>
            <span>风格解析</span>
            <span>模仿指南</span>
          </div>
        </div>
        <div class="hero-features">
          <div class="feature-card" v-for="feature in features" :key="feature.title">
            <span class="feature-icon">{{ feature.icon }}</span>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Analysis Form -->
    <section class="analysis-panel">
      <div class="panel-header">
        <h2>开始分析账号</h2>
        <p>选择平台并输入账号ID或主页链接，开始AI深度分析</p>
      </div>

      <div class="platform-selector">
        <div 
          v-for="platform in platforms" 
          :key="platform.id"
          :class="['platform-card', { active: selectedPlatform === platform.id }]"
          @click="selectedPlatform = platform.id"
        >
          <span class="platform-icon">{{ platform.icon }}</span>
          <span class="platform-name">{{ platform.name }}</span>
        </div>
      </div>

      <div class="input-section">
        <div class="input-wrapper">
          <label for="account-id">账号ID / 主页链接</label>
          <div class="input-field">
            <input
              id="account-id"
              v-model="accountId"
              type="text"
              :placeholder="currentPlatform?.input_hint || '请输入账号ID或主页链接'"
              @keyup.enter="handleAnalyze"
            />
            <button class="paste-btn" @click="pasteFromClipboard">📋 粘贴</button>
          </div>
        </div>

        <div class="options-row">
          <label class="checkbox-label">
            <input type="checkbox" v-model="includeVideos" />
            <span>获取近期作品数据</span>
          </label>
          <div class="video-count" v-if="includeVideos">
            <span>作品数量：</span>
            <select v-model="videoCount">
              <option :value="5">5个</option>
              <option :value="10">10个</option>
              <option :value="20">20个</option>
            </select>
          </div>
        </div>

        <button 
          class="analyze-button" 
          :disabled="loading || !accountId.trim()"
          @click="handleAnalyze"
        >
          <span v-if="loading" class="loading-spinner"></span>
          {{ loading ? '正在分析...' : '🚀 开始分析' }}
        </button>
      </div>

      <div v-if="error" class="feedback error">{{ error }}</div>
      <div v-if="success" class="feedback success">{{ success }}</div>
    </section>

    <!-- Task Progress -->
    <section v-if="currentTask" class="progress-section">
      <div class="progress-card">
        <div class="progress-header">
          <h3>分析进度</h3>
          <span :class="['status-badge', currentTask.status]">{{ getStatusText(currentTask.status) }}</span>
        </div>
        <div class="progress-bar-container">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: (currentTask.progress * 100) + '%' }"></div>
          </div>
          <span class="progress-text">{{ Math.round(currentTask.progress * 100) }}%</span>
        </div>
        <p class="progress-message">{{ currentTask.message }}</p>
      </div>
    </section>

    <!-- Analysis Result -->
    <section v-if="analysisResult" class="result-section">
      <!-- Account Overview -->
      <div class="result-card account-overview">
        <div class="account-header">
          <img :src="analysisResult.account_info?.avatar || defaultAvatar" class="avatar" />
          <div class="account-info">
            <h2>{{ analysisResult.account_info?.nickname || '未知账号' }}</h2>
            <p class="signature">{{ analysisResult.account_info?.signature || '暂无简介' }}</p>
            <div class="account-tags">
              <span class="platform-tag">{{ getPlatformName(analysisResult.account_info?.platform) }}</span>
              <span v-if="analysisResult.account_info?.is_verified" class="verified-tag">✅ 已认证</span>
            </div>
          </div>
        </div>
        
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-value">{{ formatNumber(analysisResult.account_info?.follower_count) }}</span>
            <span class="stat-label">粉丝</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ formatNumber(analysisResult.account_info?.like_count) }}</span>
            <span class="stat-label">获赞</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ analysisResult.account_info?.video_count || 0 }}</span>
            <span class="stat-label">作品</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ analysisResult.engagement_rate || 0 }}%</span>
            <span class="stat-label">互动率</span>
          </div>
        </div>
      </div>

      <!-- Data Metrics -->
      <div class="result-card metrics-card">
        <h3>📊 数据指标</h3>
        <div class="metrics-grid">
          <div class="metric-item">
            <span class="metric-icon">👁️</span>
            <div class="metric-content">
              <span class="metric-value">{{ formatNumber(analysisResult.average_views) }}</span>
              <span class="metric-label">平均播放</span>
            </div>
          </div>
          <div class="metric-item">
            <span class="metric-icon">❤️</span>
            <div class="metric-content">
              <span class="metric-value">{{ formatNumber(analysisResult.average_likes) }}</span>
              <span class="metric-label">平均点赞</span>
            </div>
          </div>
          <div class="metric-item">
            <span class="metric-icon">📅</span>
            <div class="metric-content">
              <span class="metric-value">{{ analysisResult.content_frequency || '未知' }}</span>
              <span class="metric-label">更新频率</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Style Analysis -->
      <div class="result-card style-card">
        <h3>🎨 创作风格</h3>
        <p class="style-description">{{ analysisResult.creation_style || '暂无分析' }}</p>
        
        <div class="themes-section" v-if="analysisResult.content_themes?.length">
          <h4>内容主题</h4>
          <div class="theme-tags">
            <span v-for="theme in analysisResult.content_themes" :key="theme" class="theme-tag">
              {{ theme }}
            </span>
          </div>
        </div>

        <div class="audience-section" v-if="analysisResult.target_audience">
          <h4>目标受众</h4>
          <p>{{ analysisResult.target_audience }}</p>
        </div>
      </div>

      <!-- Unique Points -->
      <div class="result-card points-card" v-if="analysisResult.unique_selling_points?.length">
        <h3>✨ 独特卖点</h3>
        <ul class="points-list">
          <li v-for="(point, index) in analysisResult.unique_selling_points" :key="index">
            <span class="point-number">{{ index + 1 }}</span>
            <span>{{ point }}</span>
          </li>
        </ul>
      </div>

      <!-- Viral Secrets -->
      <div class="result-card secrets-card" v-if="analysisResult.viral_secrets?.length">
        <h3>🔥 爆款秘诀</h3>
        <div class="secrets-grid">
          <div v-for="(secret, index) in analysisResult.viral_secrets" :key="index" class="secret-item">
            <span class="secret-icon">💡</span>
            <p>{{ secret }}</p>
          </div>
        </div>
      </div>

      <!-- Imitation Tips -->
      <div class="result-card tips-card" v-if="analysisResult.imitation_tips?.length">
        <h3>📝 模仿建议</h3>
        <div class="tips-list">
          <div v-for="(tip, index) in analysisResult.imitation_tips" :key="index" class="tip-item">
            <span class="tip-index">{{ index + 1 }}</span>
            <p>{{ tip }}</p>
          </div>
        </div>
      </div>

      <!-- Full Report -->
      <div class="result-card report-card" v-if="analysisResult.full_report">
        <div class="report-header">
          <h3>📄 完整分析报告</h3>
          <button class="copy-btn" @click="copyReport">复制报告</button>
        </div>
        <div class="report-content" v-html="renderMarkdown(analysisResult.full_report)"></div>
      </div>

      <!-- Recent Videos -->
      <div class="result-card videos-card" v-if="analysisResult.recent_videos?.length">
        <h3>🎬 近期作品</h3>
        <div class="videos-grid">
          <a 
            v-for="video in analysisResult.recent_videos" 
            :key="video.video_id" 
            :href="video.video_url || getVideoUrl(video)"
            target="_blank"
            rel="noopener noreferrer"
            class="video-item"
          >
            <div class="video-cover-wrapper">
              <img 
                :src="video.cover || defaultCover" 
                class="video-cover" 
                @error="handleImageError"
                loading="lazy"
              />
              <div class="video-overlay">
                <span class="play-icon">▶</span>
              </div>
              <span v-if="video.duration" class="video-duration">{{ formatDuration(video.duration) }}</span>
            </div>
            <div class="video-info">
              <h4 :title="video.title">{{ video.title || '无标题' }}</h4>
              <div class="video-stats">
                <span>👁️ {{ formatNumber(video.play_count) }}</span>
                <span>❤️ {{ formatNumber(video.like_count) }}</span>
                <span>💬 {{ formatNumber(video.comment_count) }}</span>
              </div>
              <span class="video-date">{{ video.create_time }}</span>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- Empty State -->
    <section v-if="!currentTask && !analysisResult" class="empty-state">
      <div class="empty-card">
        <div class="empty-icon">🔍</div>
        <h3>开始你的第一次账号分析</h3>
        <p>选择平台，输入账号信息，让AI帮你深度解析优秀创作者的成功密码</p>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { resolveApiUrl } from '@/utils/api'

// Types
interface Platform {
  id: string
  name: string
  icon: string
  description: string
  input_hint: string
}

interface AccountInfo {
  platform: string
  account_id: string
  nickname: string
  avatar: string
  signature: string
  follower_count: number
  following_count: number
  like_count: number
  video_count: number
  is_verified: boolean
}

interface VideoStats {
  video_id: string
  title: string
  cover: string
  play_count: number
  like_count: number
  comment_count: number
  share_count: number
  create_time: string
  video_url?: string  // 视频链接
  platform?: string   // 平台标识
  duration?: number   // 视频时长（秒）
}

interface AnalysisResult {
  account_info: AccountInfo
  recent_videos: VideoStats[]
  engagement_rate: number
  average_views: number
  average_likes: number
  content_frequency: string
  creation_style: string
  content_themes: string[]
  target_audience: string
  unique_selling_points: string[]
  viral_secrets: string[]
  improvement_suggestions: string[]
  imitation_tips: string[]
  full_report: string
}

interface TaskStatus {
  task_id: string
  status: string
  progress: number
  message: string
  result?: AnalysisResult
}

// Data
const platforms = ref<Platform[]>([
  { id: 'douyin', name: '抖音', icon: '🎵', description: '支持抖音账号分析', input_hint: '输入抖音号或主页链接' },
  { id: 'bilibili', name: 'B站', icon: '📺', description: '支持B站UP主分析', input_hint: '输入UID或空间链接' },
  { id: 'xiaohongshu', name: '小红书', icon: '📕', description: '支持小红书博主分析', input_hint: '输入小红书号或主页链接' }
])

const features = [
  { icon: '📊', title: '数据洞察', desc: '粉丝增长、互动率、播放量全面分析' },
  { icon: '🎨', title: '风格解析', desc: '创作风格、内容主题、受众画像' },
  { icon: '💡', title: '爆款秘诀', desc: '揭示高流量内容的成功公式' }
]

const selectedPlatform = ref('douyin')
const accountId = ref('')
const includeVideos = ref(true)
const videoCount = ref(10)
const loading = ref(false)
const error = ref('')
const success = ref('')
const currentTask = ref<TaskStatus | null>(null)
const analysisResult = ref<AnalysisResult | null>(null)

const defaultAvatar = 'https://via.placeholder.com/100x100.png?text=Avatar'
const defaultCover = 'https://via.placeholder.com/200x120.png?text=Video'

let pollInterval: number | undefined

// Computed
const currentPlatform = computed(() => platforms.value.find(p => p.id === selectedPlatform.value))

// Methods
const pasteFromClipboard = async () => {
  try {
    if (navigator.clipboard) {
      const text = await navigator.clipboard.readText()
      if (text) {
        accountId.value = text.trim()
      }
    }
  } catch (e) {
    console.debug('Clipboard access denied')
  }
}

const handleAnalyze = async () => {
  if (!accountId.value.trim()) {
    error.value = '请输入账号ID或主页链接'
    return
  }

  loading.value = true
  error.value = ''
  success.value = ''
  analysisResult.value = null
  currentTask.value = null

  try {
    const response = await fetch(resolveApiUrl('/api/account/analyze'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platform: selectedPlatform.value,
        account_id: accountId.value,
        include_videos: includeVideos.value,
        video_count: videoCount.value
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || '分析请求失败')
    }

    const taskData = await response.json()
    currentTask.value = taskData
    success.value = '分析任务已创建，正在处理中...'

    // Start polling for status
    startPolling(taskData.task_id)

  } catch (err) {
    error.value = err instanceof Error ? err.message : '分析失败'
  } finally {
    loading.value = false
  }
}

const startPolling = (taskId: string) => {
  if (pollInterval) {
    clearInterval(pollInterval)
  }

  pollInterval = window.setInterval(async () => {
    try {
      const response = await fetch(resolveApiUrl(`/api/account/status/${taskId}`))
      if (response.ok) {
        const status = await response.json()
        currentTask.value = status

        if (status.status === 'completed') {
          stopPolling()
          analysisResult.value = status.result
          success.value = '分析完成！'
          currentTask.value = null
        } else if (status.status === 'failed') {
          stopPolling()
          error.value = status.message || '分析失败'
          currentTask.value = null
        }
      }
    } catch (e) {
      console.error('Polling error:', e)
    }
  }, 2000)
}

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = undefined
  }
}

const getStatusText = (status: string): string => {
  const map: Record<string, string> = {
    pending: '等待中',
    running: '分析中',
    completed: '已完成',
    failed: '失败'
  }
  return map[status] || status
}

const getPlatformName = (platform?: string): string => {
  const map: Record<string, string> = {
    douyin: '抖音',
    bilibili: 'B站',
    xiaohongshu: '小红书'
  }
  return platform ? (map[platform] || platform) : '未知'
}

const formatNumber = (value?: number): string => {
  if (!value) return '0'
  if (value >= 100000000) return (value / 100000000).toFixed(1) + '亿'
  if (value >= 10000) return (value / 10000).toFixed(1) + '万'
  return value.toLocaleString('zh-CN')
}

const formatDuration = (seconds?: number): string => {
  if (!seconds) return ''
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const getVideoUrl = (video: VideoStats): string => {
  // 根据平台生成视频链接
  if (video.video_url) return video.video_url
  const platform = video.platform || selectedPlatform.value
  const vid = video.video_id
  switch (platform) {
    case 'bilibili':
      return vid.startsWith('BV') ? `https://www.bilibili.com/video/${vid}` : `https://www.bilibili.com/video/av${vid}`
    case 'douyin':
      return `https://www.douyin.com/video/${vid}`
    case 'xiaohongshu':
      return `https://www.xiaohongshu.com/explore/${vid}`
    case 'youtube':
      return `https://www.youtube.com/watch?v=${vid}`
    case 'tiktok':
      return `https://www.tiktok.com/@/video/${vid}`
    case 'kuaishou':
      return `https://www.kuaishou.com/short-video/${vid}`
    default:
      return '#'
  }
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = defaultCover
}

const renderMarkdown = (text: string): string => {
  // Simple markdown rendering
  return text
    .replace(/## (.+)/g, '<h2>$1</h2>')
    .replace(/### (.+)/g, '<h3>$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>')
    .replace(/- (.+)/g, '<li>$1</li>')
}

const copyReport = async () => {
  if (analysisResult.value?.full_report) {
    try {
      await navigator.clipboard.writeText(analysisResult.value.full_report)
      success.value = '报告已复制到剪贴板'
      setTimeout(() => { success.value = '' }, 2000)
    } catch (e) {
      error.value = '复制失败'
    }
  }
}

onMounted(() => {
  // Load supported platforms from API
  fetch(resolveApiUrl('/api/account/platforms'))
    .then(res => res.json())
    .then(data => {
      if (data.platforms) {
        platforms.value = data.platforms
      }
    })
    .catch(console.error)
})

onBeforeUnmount(() => {
  stopPolling()
})
</script>

<style scoped>
.account-analysis-page {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  padding-bottom: 4rem;
}

/* Hero Section */
.hero {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  border-radius: 0 0 48px 48px;
  padding: 4rem 3rem 5rem;
  color: #ffffff;
  position: relative;
  overflow: hidden;
}

.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 20% 30%, rgba(255,255,255,0.2), transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(255,255,255,0.15), transparent 50%);
  pointer-events: none;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  position: relative;
  z-index: 1;
}

.hero-text h1 {
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 1.2rem;
}

.hero-text p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: rgba(255,255,255,0.9);
  margin-bottom: 1.5rem;
}

.hero-badges {
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
}

.hero-badges span {
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.9rem;
}

.hero-features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.feature-card {
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 16px;
  padding: 1.2rem;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 1rem;
}

.feature-icon {
  font-size: 1.8rem;
}

.feature-card h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.feature-card p {
  margin: 0.3rem 0 0;
  font-size: 0.85rem;
  color: rgba(255,255,255,0.8);
}

/* Analysis Panel */
.analysis-panel {
  max-width: 900px;
  margin: -3rem auto 0;
  padding: 0 1.5rem;
  position: relative;
  z-index: 10;
}

.analysis-panel > .panel-header,
.analysis-panel > .platform-selector,
.analysis-panel > .input-section,
.analysis-panel > .feedback {
  background: #ffffff;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 20px 50px rgba(102, 126, 234, 0.15);
}

.analysis-panel > .panel-header {
  border-radius: 20px 20px 0 0;
  padding-bottom: 1rem;
}

.analysis-panel > .platform-selector {
  border-radius: 0;
  padding-top: 0;
  padding-bottom: 1rem;
}

.analysis-panel > .input-section {
  border-radius: 0 0 20px 20px;
  padding-top: 1rem;
}

.panel-header h2 {
  margin: 0 0 0.5rem;
  font-size: 1.6rem;
  color: #1f2937;
}

.panel-header p {
  margin: 0;
  color: #6b7280;
}

.platform-selector {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  background: #fff;
}

.platform-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.2rem;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.platform-card:hover {
  border-color: #a78bfa;
  background: #faf5ff;
}

.platform-card.active {
  border-color: #7c3aed;
  background: linear-gradient(135deg, #f3e8ff, #faf5ff);
  box-shadow: 0 4px 20px rgba(124, 58, 237, 0.2);
}

.platform-icon {
  font-size: 2rem;
}

.platform-name {
  font-weight: 600;
  color: #374151;
}

.input-section {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  background: #fff;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-wrapper label {
  font-weight: 600;
  color: #374151;
}

.input-field {
  display: flex;
  gap: 0.8rem;
}

.input-field input {
  flex: 1;
  padding: 1rem 1.2rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.input-field input:focus {
  outline: none;
  border-color: #7c3aed;
  box-shadow: 0 0 0 4px rgba(124, 58, 237, 0.1);
}

.paste-btn {
  padding: 0 1.2rem;
  background: #f3e8ff;
  border: none;
  border-radius: 12px;
  color: #7c3aed;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.paste-btn:hover {
  background: #ede9fe;
}

.options-row {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #7c3aed;
}

.video-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.video-count select {
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.analyze-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.analyze-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
}

.analyze-button:disabled {
  background: #c4b5fd;
  cursor: not-allowed;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.feedback {
  padding: 1rem 1.5rem;
  border-radius: 12px;
  margin-top: 1rem;
}

.feedback.error {
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.feedback.success {
  background: #dcfce7;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

/* Progress Section */
.progress-section {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.progress-card {
  background: #fff;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 40px rgba(0,0,0,0.08);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.progress-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

.status-badge {
  padding: 0.4rem 1rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-badge.running {
  background: #dbeafe;
  color: #2563eb;
}

.status-badge.completed {
  background: #dcfce7;
  color: #16a34a;
}

.status-badge.failed {
  background: #fee2e2;
  color: #dc2626;
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.progress-bar {
  flex: 1;
  height: 10px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.progress-text {
  font-weight: 600;
  color: #7c3aed;
}

.progress-message {
  margin: 1rem 0 0;
  color: #6b7280;
}

/* Result Section */
.result-section {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: grid;
  gap: 1.5rem;
}

.result-card {
  background: #fff;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 40px rgba(0,0,0,0.06);
}

.result-card h3 {
  margin: 0 0 1.2rem;
  font-size: 1.3rem;
  color: #1f2937;
}

/* Account Overview */
.account-overview {
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
}

.account-header {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.account-info h2 {
  margin: 0 0 0.3rem;
  font-size: 1.5rem;
}

.signature {
  margin: 0 0 0.8rem;
  color: #6b7280;
  font-size: 0.95rem;
}

.account-tags {
  display: flex;
  gap: 0.5rem;
}

.platform-tag {
  background: #7c3aed;
  color: #fff;
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
  font-size: 0.8rem;
}

.verified-tag {
  background: #dcfce7;
  color: #16a34a;
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
  font-size: 0.8rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.stat-item {
  text-align: center;
  padding: 1rem;
  background: #fff;
  border-radius: 12px;
}

.stat-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #7c3aed;
}

.stat-label {
  font-size: 0.85rem;
  color: #6b7280;
}

/* Metrics Card */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.2rem;
  background: #f8fafc;
  border-radius: 12px;
}

.metric-icon {
  font-size: 1.5rem;
}

.metric-value {
  display: block;
  font-size: 1.3rem;
  font-weight: 600;
  color: #1f2937;
}

.metric-label {
  font-size: 0.85rem;
  color: #6b7280;
}

/* Style Card */
.style-description {
  font-size: 1.1rem;
  line-height: 1.7;
  color: #374151;
  margin-bottom: 1.5rem;
}

.themes-section h4,
.audience-section h4 {
  font-size: 1rem;
  margin: 0 0 0.8rem;
  color: #6b7280;
}

.theme-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.theme-tag {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #92400e;
  padding: 0.4rem 1rem;
  border-radius: 999px;
  font-size: 0.9rem;
}

/* Points Card */
.points-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.points-list li {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: #f0fdf4;
  border-radius: 12px;
}

.point-number {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #16a34a;
  color: #fff;
  border-radius: 50%;
  font-weight: 600;
  flex-shrink: 0;
}

/* Secrets Card */
.secrets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.secret-item {
  display: flex;
  gap: 1rem;
  padding: 1.2rem;
  background: linear-gradient(135deg, #fef2f2, #fee2e2);
  border-radius: 12px;
}

.secret-icon {
  font-size: 1.5rem;
}

.secret-item p {
  margin: 0;
  color: #991b1b;
  line-height: 1.6;
}

/* Tips Card */
.tips-list {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.tip-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: #eff6ff;
  border-radius: 12px;
}

.tip-index {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2563eb;
  color: #fff;
  border-radius: 50%;
  font-weight: 600;
  flex-shrink: 0;
}

.tip-item p {
  margin: 0;
  color: #1e40af;
  line-height: 1.6;
}

/* Report Card */
.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.copy-btn {
  padding: 0.5rem 1rem;
  background: #f3e8ff;
  color: #7c3aed;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
}

.copy-btn:hover {
  background: #ede9fe;
}

.report-content {
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  line-height: 1.8;
  color: #374151;
}

.report-content :deep(h2) {
  font-size: 1.3rem;
  margin: 1.5rem 0 0.8rem;
  color: #1f2937;
}

.report-content :deep(h3) {
  font-size: 1.1rem;
  margin: 1.2rem 0 0.6rem;
  color: #374151;
}

/* Videos Card */
.videos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.2rem;
}

.video-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  transition: all 0.25s ease;
  text-decoration: none;
  color: inherit;
  border: 1px solid transparent;
}

.video-item:hover {
  background: #fff;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  border-color: #e5e7eb;
  transform: translateY(-2px);
}

.video-cover-wrapper {
  position: relative;
  width: 140px;
  height: 80px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
}

.video-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.video-item:hover .video-cover {
  transform: scale(1.05);
}

.video-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.video-item:hover .video-overlay {
  opacity: 1;
}

.play-icon {
  width: 36px;
  height: 36px;
  background: rgba(255,255,255,0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: #7c3aed;
  padding-left: 3px;
}

.video-duration {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0,0,0,0.75);
  color: #fff;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
}

.video-info {
  flex: 1;
  min-width: 0;
}

.video-info h4 {
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  color: #1f2937;
  font-weight: 500;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.video-stats {
  display: flex;
  gap: 0.8rem;
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.4rem;
  flex-wrap: wrap;
}

.video-stats span {
  display: flex;
  align-items: center;
  gap: 0.2rem;
}

.video-date {
  font-size: 0.75rem;
  color: #9ca3af;
}

/* Empty State */
.empty-state {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.empty-card {
  text-align: center;
  padding: 4rem 2rem;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.06);
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-card h3 {
  margin: 0 0 0.5rem;
  font-size: 1.4rem;
  color: #1f2937;
}

.empty-card p {
  margin: 0;
  color: #6b7280;
}

/* Responsive */
@media (max-width: 768px) {
  .hero-content {
    grid-template-columns: 1fr;
  }
  
  .hero-text h1 {
    font-size: 2rem;
  }
  
  .platform-selector {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
}
</style>
